import { record } from 'rrweb';
import { api, ApiError } from '../api/client';
import { PRIVATE } from './share';

/**
 * Session recording, for people a company's admins chose to record (they are told first, and see
 * a note while it runs). rrweb copies the page, not the screen: every typed value is masked,
 * anything marked private is left out, and live help pauses it (live help is never recorded).
 * Pieces are gzipped here and sent every few seconds.
 */

const FLUSH_MS = 10_000;
/** A piece the browser may still send as the tab closes (keepalive's limit is 64 KB). */
const EXIT_LIMIT = 60_000;

export interface SessionRecorder {
  stop(): void;
}

async function gzip(events: unknown[]): Promise<Blob> {
  const body = new Blob([JSON.stringify(events)]).stream().pipeThrough(new CompressionStream('gzip'));
  return new Response(body).blob();
}

export function startSessionRecording(domain: string, onEnded: () => void): SessionRecorder {
  let id: string | null = null;
  let seq = 0;
  let queue: unknown[] = [];
  let sending = false;
  let stopped = false;
  let paused = false;
  let stopRecording: (() => void) | undefined;
  let failures = 0;

  const page = () => location.pathname.slice(0, 200);

  const end = () => {
    if (stopped) return;
    stopped = true;
    stopRecording?.();
    stopRecording = undefined;
    window.clearInterval(timer);
    window.removeEventListener('pagehide', onExit);
    document.removeEventListener('visibilitychange', onHidden);
    window.removeEventListener('stitch:live-share', onLive);
    onEnded();
  };

  const flush = async () => {
    if (!id || sending || !queue.length) return;
    sending = true;
    const batch = queue;
    queue = [];
    const n = seq++;
    try {
      await api.recordingChunk(domain, id, n, await gzip(batch), page());
      failures = 0;
    } catch (err) {
      // Switched off, signed out, or ended: stop quietly.
      if (err instanceof ApiError && [401, 403, 404, 409, 410].includes(err.status)) return end();
      // A blip: try the same piece again next time, unless it keeps failing.
      seq = n;
      queue = batch.concat(queue);
      if (++failures > 6 || queue.length > 20_000) end();
    } finally {
      sending = false;
    }
  };

  // Leaving the page: what's left goes with the browser's keepalive, if it's small enough.
  const onExit = () => {
    if (!id || !queue.length) return;
    const batch = queue;
    queue = [];
    const n = seq++;
    void gzip(batch).then((gz) => {
      if (gz.size <= EXIT_LIMIT && id) api.recordingChunkOnExit(domain, id, n, gz, page());
    });
  };
  const onHidden = () => {
    if (document.visibilityState === 'hidden') void flush();
  };

  const begin = () => {
    stopRecording = record({
      emit(e) {
        queue.push(e);
      },
      // Sound and video aren't recorded (and a replay must never fetch them as whoever watches it).
      blockSelector: `${PRIVATE}, audio, video`,
      maskAllInputs: true,
      maskInputOptions: { password: true },
      inlineStylesheet: true,
      // A fresh full copy now and then, so a long recording can be watched from the middle.
      checkoutEveryNms: 5 * 60_000,
      sampling: { mousemove: 100, scroll: 150, input: 'last' },
      recordCanvas: false,
      collectFonts: false
    });
  };

  // Live help shares the page with rrweb too: this steps aside while it runs, and starts afresh after.
  const onLive = (ev: Event) => {
    const on = (ev as CustomEvent<{ on: boolean }>).detail?.on;
    if (on && !paused) {
      paused = true;
      void flush();
      stopRecording?.();
      stopRecording = undefined;
    } else if (!on && paused && !stopped) {
      paused = false;
      begin();
    }
  };

  const timer = window.setInterval(() => void flush(), FLUSH_MS);
  window.addEventListener('pagehide', onExit);
  document.addEventListener('visibilitychange', onHidden);
  window.addEventListener('stitch:live-share', onLive);

  void api
    .recordingBegin(domain)
    .then((r) => {
      if (stopped) return;
      id = r.id;
      if (!paused) begin();
    })
    .catch(() => end());

  return {
    stop() {
      void flush().finally(end);
    }
  };
}
